import React, { useState, useEffect } from 'react';
import { db } from '../firebase/firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';

/**
 * Address Details Form Component
 * Collects additional address information after map selection
 * Mobile-responsive, modern design matching your app style
 */
const AddressDetailsForm = ({
  selectedLocation,
  selectedVillage,
  onSave,
  onCancel,
  isLoading = false,
  hideVillageField = false,
  initialData = {}
}) => {
  // Ensure initialData is not null and has default values
  const safeInitialData = initialData || {};
  
  const [addressDetails, setAddressDetails] = useState({
    label: safeInitialData.label || 'Home',
    userName: safeInitialData.userName || '',
    houseNo: safeInitialData.houseNo || '',
    building: safeInitialData.building || '',
    landmark: safeInitialData.landmark || '',
    instructions: safeInitialData.instructions || '',
    village: selectedVillage || safeInitialData.village || '',
    ...safeInitialData
  });

  const [villages, setVillages] = useState([]);
  const [loadingVillages, setLoadingVillages] = useState(true);
  const [errors, setErrors] = useState({});

  const addressLabels = [
    { value: 'Home', icon: '🏠', color: '#2A632D' },
    { value: 'Work', icon: '🏢', color: '#1976D2' },
    { value: 'Other', icon: '📍', color: '#FF6B35' }
  ];

  // Fetch villages from Firestore on component mount
  useEffect(() => {
    const fetchVillages = async () => {
      try {
        setLoadingVillages(true);
        const path = "/in/uttar pradesh/division/moradabad/district/amroha/sub-district/dhanaura/village/villages";
        const docRef = doc(db, ...path.split('/'));
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists() && docSnap.data().data) {
          const villageData = docSnap.data().data;
          const villageList = Object.keys(villageData)
            .map(name => ({
              name,
              ...villageData[name]
            }))
            .sort((a, b) => a.name.localeCompare(b.name));
          
          setVillages(villageList);
        } else {
          console.error('Village data not found');
          setVillages([]);
        }
      } catch (error) {
        console.error('Error fetching villages:', error);
        setVillages([]);
      } finally {
        setLoadingVillages(false);
      }
    };

    fetchVillages();
  }, []);

  const handleInputChange = (field, value) => {
    setAddressDetails(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Only userName is mandatory
    if (!addressDetails.userName.trim()) {
      newErrors.userName = 'Name is required';
    }

    // Village is mandatory only when shown (not hidden)
    if (!hideVillageField && (!addressDetails.village || addressDetails.village === '')) {
      newErrors.village = 'Please select a village';
    }
    
    // All other fields are optional - no validation needed
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) return;
    
    const completeAddress = {
      ...selectedLocation,
      ...addressDetails,
      fullAddress: `${addressDetails.houseNo}, ${addressDetails.building}, ${selectedLocation?.formatted || ''}`,
      createdAt: new Date().toISOString()
    };
    
    onSave(completeAddress);
  };

  if (!selectedLocation) {
    return (
      <div className="text-center py-4">
        <p className="text-muted">Please select a location on the map first</p>
      </div>
    );
  }

  return (
    <div className="address-details-form">
      {/* Header */}
      <div className="mb-4">
        <h5 className="fw-bold text-dark mb-2">Add Address Details</h5>
        <div className="d-flex align-items-start">
          <span className="text-success me-2 mt-1">📍</span>
          <div>
            <p className="mb-1 text-dark">{selectedLocation.formatted}</p>
            <small className="text-muted">
              {selectedLocation.area && `${selectedLocation.area}, `}
              {selectedLocation.city}
              {selectedLocation.pincode && ` - ${selectedLocation.pincode}`}
            </small>
          </div>
        </div>
      </div>

      {/* Address Type Selection */}
      <div className="mb-4">
        <label className="form-label fw-semibold text-dark">Address Type</label>
        <div className="row g-2">
          {addressLabels.map(label => (
            <div key={label.value} className="col-4">
              <input
                type="radio"
                className="btn-check"
                name="addressLabel"
                id={`label-${label.value}`}
                value={label.value}
                checked={addressDetails.label === label.value}
                onChange={(e) => handleInputChange('label', e.target.value)}
              />
              <label
                className={`btn btn-outline-success w-100 d-flex flex-column align-items-center py-3 ${
                  addressDetails.label === label.value ? 'active' : ''
                }`}
                htmlFor={`label-${label.value}`}
                style={{
                  borderRadius: '8px',
                  borderWidth: '2px',
                  ...(addressDetails.label === label.value && {
                    backgroundColor: label.color,
                    borderColor: label.color,
                    color: 'white'
                  })
                }}
              >
                <span style={{ fontSize: '20px' }}>{label.icon}</span>
                <small className="mt-1">{label.value}</small>
              </label>
            </div>
          ))}
        </div>
      </div>

      {/* User Name */}
      <div className="mb-3">
        <label htmlFor="userName" className="form-label fw-semibold text-dark">
          Full Name <span className="text-danger">*</span>
        </label>
        <input
          type="text"
          id="userName"
          className={`form-control ${errors.userName ? 'is-invalid' : ''}`}
          placeholder="Enter your full name"
          value={addressDetails.userName}
          onChange={(e) => handleInputChange('userName', e.target.value)}
          style={{
            borderRadius: '8px',
            padding: '12px 16px',
            fontSize: '16px',
            border: '2px solid #e9ecef'
          }}
        />
        {errors.userName && (
          <div className="invalid-feedback">{errors.userName}</div>
        )}
      </div>

      {/* Village Selection - Only show if not already selected in previous step */}
      {!hideVillageField && (
        <div className="mb-3">
          <label htmlFor="village" className="form-label fw-semibold text-dark">
            Select Village/Area <span className="text-danger">*</span>
          </label>
          {loadingVillages ? (
            <div className="text-center py-3">
              <div className="spinner-border spinner-border-sm text-primary" role="status">
                <span className="visually-hidden">Loading villages...</span>
              </div>
              <p className="text-muted small mt-2">Loading villages...</p>
            </div>
          ) : (
            <>
              <select
                id="village"
                className={`form-select ${errors.village ? 'is-invalid' : ''}`}
                value={addressDetails.village}
                onChange={(e) => handleInputChange('village', e.target.value)}
                style={{
                  borderRadius: '8px',
                  padding: '12px 16px',
                  fontSize: '16px',
                  border: '2px solid #e9ecef'
                }}
              >
                <option value="">Select Village</option>
                {villages.map((village) => (
                  <option key={village.name} value={village.name}>
                    {village.name}
                  </option>
                ))}
              </select>
              {errors.village && (
                <div className="invalid-feedback d-block">{errors.village}</div>
              )}
            </>
          )}
        </div>
      )}

      {/* House Number */}
      <div className="mb-3">
        <label htmlFor="houseNo" className="form-label fw-semibold text-dark">
          House No. & Floor <span className="text-muted">(Optional)</span>
        </label>
        <input
          type="text"
          id="houseNo"
          className={`form-control ${errors.houseNo ? 'is-invalid' : ''}`}
          placeholder="Enter flat/house number"
          value={addressDetails.houseNo}
          onChange={(e) => handleInputChange('houseNo', e.target.value)}
          style={{
            borderRadius: '8px',
            padding: '12px 16px',
            fontSize: '16px',
            border: '2px solid #e9ecef'
          }}
        />
        {errors.houseNo && (
          <div className="invalid-feedback">{errors.houseNo}</div>
        )}
      </div>

      {/* Building Name */}
      <div className="mb-3">
        <label htmlFor="building" className="form-label fw-semibold text-dark">
          Building & Society <span className="text-muted">(Optional)</span>
        </label>
        <input
          type="text"
          id="building"
          className={`form-control ${errors.building ? 'is-invalid' : ''}`}
          placeholder="Enter building/society name"
          value={addressDetails.building}
          onChange={(e) => handleInputChange('building', e.target.value)}
          style={{
            borderRadius: '8px',
            padding: '12px 16px',
            fontSize: '16px',
            border: '2px solid #e9ecef'
          }}
        />
        {errors.building && (
          <div className="invalid-feedback">{errors.building}</div>
        )}
      </div>

      {/* Landmark */}
      <div className="mb-3">
        <label htmlFor="landmark" className="form-label fw-semibold text-dark">
          Landmark & Area Name <span className="text-muted">(Optional)</span>
        </label>
        <input
          type="text"
          id="landmark"
          className="form-control"
          placeholder="Nearby landmark (e.g., Near XYZ Mall)"
          value={addressDetails.landmark}
          onChange={(e) => handleInputChange('landmark', e.target.value)}
          style={{
            borderRadius: '8px',
            padding: '12px 16px',
            fontSize: '16px',
            border: '2px solid #e9ecef'
          }}
        />
      </div>

      {/* Delivery Instructions */}
      <div className="mb-4">
        <label htmlFor="instructions" className="form-label fw-semibold text-dark">
          Delivery Instructions <span className="text-muted">(Optional)</span>
        </label>
        <textarea
          id="instructions"
          className="form-control"
          rows="3"
          placeholder="Any specific instructions for delivery (e.g., Ring bell twice, Call on arrival)"
          value={addressDetails.instructions}
          onChange={(e) => handleInputChange('instructions', e.target.value)}
          style={{
            borderRadius: '8px',
            padding: '12px 16px',
            fontSize: '16px',
            border: '2px solid #e9ecef',
            resize: 'vertical'
          }}
        />
      </div>

      {/* Action Buttons */}
      <div className="d-grid gap-2 d-md-flex justify-content-md-end">
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={onCancel}
          disabled={isLoading}
          style={{
            borderRadius: '8px',
            padding: '12px 24px',
            fontWeight: '600'
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-success"
          onClick={handleSave}
          disabled={isLoading}
          style={{
            backgroundColor: '#2A632D',
            borderColor: '#2A632D',
            borderRadius: '8px',
            padding: '12px 24px',
            fontWeight: '600',
            minWidth: '120px'
          }}
        >
          {isLoading ? (
            <>
              <div className="spinner-border spinner-border-sm me-2" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              Saving...
            </>
          ) : (
            'Save Address'
          )}
        </button>
      </div>

      <style jsx>{`
        .address-details-form {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }
        
        .form-control:focus {
          border-color: #2A632D;
          box-shadow: 0 0 0 0.2rem rgba(42, 99, 45, 0.25);
        }
        
        .btn-check:checked + .btn {
          transform: scale(0.98);
          transition: transform 0.1s ease;
        }
        
        .btn-outline-success:hover {
          background-color: #2A632D;
          border-color: #2A632D;
        }
        
        @media (max-width: 768px) {
          .form-control {
            font-size: 16px; /* Prevents zoom on iOS */
          }
          
          .d-grid.gap-2.d-md-flex {
            display: grid !important;
          }
          
          .btn {
            padding: 14px 24px;
          }
        }
      `}</style>
    </div>
  );
};

export default AddressDetailsForm;
